import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import httpClient from '../utils/httpClient';

interface User {
  id: string;
  phone: string;
  nickname?: string;
  avatar?: string;
  status: 'online' | 'offline' | 'busy';
  lastActiveAt: Date;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  loading: boolean;
  login: (phone: string, password?: string) => Promise<boolean>;
  register: (phone: string, code: string, password?: string, nickname?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  updateProfile: (data: {
    nickname?: string;
    avatar?: string;
    status?: 'online' | 'offline' | 'busy';
  }) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadAuth = async () => {
    try {
      const savedToken = await AsyncStorage.getItem('token');
      const savedUser = await AsyncStorage.getItem('user');

      if (savedToken && savedUser) {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      }
    } catch (error) {
      console.error('Load auth failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAuth();
  }, []);

  const login = async (phone: string, password?: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const authType = password ? 'password' : 'phone';
      const response = await httpClient.post('/auth/login', {
        phone,
        [authType]: password || null,
      });

      const { user, accessToken, refreshToken } = response.data;

      await AsyncStorage.setItem('token', accessToken);
      await AsyncStorage.setItem('user', JSON.stringify(user));

      setToken(accessToken);
      setUser(user);

      startTokenRefreshTimer(refreshToken);

      return true;
    } catch (error: any) {
      console.error('Login failed:', error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    phone: string,
    code: string,
    password?: string,
    nickname?: string,
  ): Promise<boolean> => {
    setIsLoading(true);
    try {
      await httpClient.post('/auth/register', {
        phone,
        code,
        password,
        nickname,
      });

      return await login(phone, password);
    } catch (error: any) {
      console.error('Register failed:', error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await AsyncStorage.multiRemove(['token', 'user']);

      setToken(null);
      setUser(null);

      if (refreshTimer !== null) {
        clearInterval(refreshTimer);
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const updateProfile = async (data: {
    nickname?: string;
    avatar?: string;
    status?: 'online' | 'offline' | 'busy';
  }): Promise<boolean> => {
    if (!token) return false;

    try {
      const response = await httpClient.patch('/user/profile', data);

      if (user) {
        const updatedUser = { ...user, ...data };
        setUser(updatedUser);
        await AsyncStorage.setItem('user', JSON.stringify(updatedUser));
      }
      return true;
    } catch (error: any) {
      console.error('Update profile failed:', error.message);
      return false;
    }
  };

  let refreshTimer: NodeJS.Timeout | null = null;

  const startTokenRefreshTimer = (refreshToken: string) => {
    refreshTimer = setInterval(async () => {
      try {
        const response = await fetch(`${process.env.API_URL}/auth/refresh`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        });

        if (response.ok) {
          const { accessToken } = await response.json();
          setToken(accessToken);
          await AsyncStorage.setItem('token', accessToken);
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
      }
    }, 7200000); // 2 hours in milliseconds
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        loading: isLoading,
        login,
        register,
        logout,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useUserStatus() {
  const { user, updateProfile } = useAuth();

  const setUserStatus = async (status: 'online' | 'offline' | 'busy') => {
    if (user) {
      await updateProfile({ status });
    }
  };

  return {
    status: user?.status || 'offline',
    isOnline: user?.status === 'online',
    isOffline: user?.status === 'offline',
    isBusy: user?.status === 'busy',
    setUserStatus,
  };
}
